import React, { useState } from 'react';
import { tapLight } from '../utils/haptics';
import '../styles/app/picks.css';

const METHODS = ['KO/TKO', 'Submission', 'Decision'];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// "Abdul Rakhman Yakhyaev" -> small "Abdul Rakhman" over a big "Yakhyaev"
// (tale-of-the-tape style). Suffixes stay with the surname ("Aldo Jr.").
const SUFFIX = /^(jr\.?|sr\.?|ii|iii|iv)$/i;
const splitName = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: '', last: parts[0] || '' };
  let cut = parts.length - 1;
  if (SUFFIX.test(parts[cut]) && cut > 1) cut -= 1;
  return { first: parts.slice(0, cut).join(' '), last: parts.slice(cut).join(' ') };
};

const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9.5 17 4 11.5" />
  </svg>
);

const CrossIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const BeltIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 9h4l2-3h8l2 3h4v6h-4l-2 3H8l-2-3H2z" />
    <circle cx="12" cy="12" r="2.6" />
  </svg>
);

function FightCard({
  fight,
  pick,
  onPick,
  onMethodPick,
  showMethod = false,
  disabled = false,
  isTitle = false,
  showResult = false,
  communityStats = null,
  championshipMultiplier = null,
  featured = false
}) {
  const {
    fighter1_name,
    fighter2_name,
    fighter1_record,
    fighter2_record,
    fighter1_image_url,
    fighter2_image_url,
    fighter1_odds,
    fighter2_odds,
    weight_class,
    winner,
    method
  } = fight;
  const rounds = fight?.rounds ?? (isTitle || fight?.is_main_event ? 5 : 3);

  const selectedWinner = pick?.winner;
  const selectedMethod = pick?.method;

  // Which fighter was just tapped: drives the one-shot "punch" animation
  const [punched, setPunched] = useState(null);

  // Check if user's pick was correct
  const isDraw = winner === 'Draw';
  const pickedCorrectWinner = winner && !isDraw && selectedWinner === winner;
  const pickedCorrectMethod = winner && !isDraw && method && selectedMethod === method;

  const getFighterClass = (fighterName) => {
    let classes = ['fighter'];

    if (showResult && winner) {
      if (isDraw) {
        classes.push('draw');
      } else if (fighterName === winner) {
        classes.push('winner');
      } else {
        classes.push('loser');
      }
      // Also mark the picked fighter when viewing results
      if (selectedWinner === fighterName) {
        classes.push('picked');
      }
    } else if (selectedWinner === fighterName) {
      classes.push('selected');
      // Add correct/incorrect styling when fight has a result
      if (winner && !isDraw) {
        if (fighterName === winner) {
          classes.push('pick-correct');
        } else {
          classes.push('pick-incorrect');
        }
      } else if (isDraw) {
        classes.push('pick-draw');
      }
    }

    return classes.join(' ');
  };

  // Render pick indicator badge on fighter photo when results are in
  const getPickBadge = (fighterName) => {
    if (!selectedWinner || selectedWinner !== fighterName || !winner) return null;

    const isCorrect = !isDraw && fighterName === winner;
    const isWrong = !isDraw && fighterName !== winner;

    let symbol, variant;
    if (isDraw) {
      symbol = '—';
      variant = 'draw';
    } else if (isCorrect) {
      symbol = <CheckIcon size={12} />;
      variant = 'correct';
    } else if (isWrong) {
      symbol = <CrossIcon size={11} />;
      variant = 'wrong';
    }

    return (
      <div className={`pk-badge pk-badge--${variant}`}>
        {symbol}
      </div>
    );
  };

  const handleFighterClick = (fighterName) => {
    if (!disabled && onPick) {
      tapLight();
      setPunched(fighterName);
      onPick(fighterName);
    }
  };

  const interactive = !disabled && !!onPick;

  const renderFighter = (corner, name, record, imageUrl, odds) => {
    const isPicked = selectedWinner === name;
    const isActualWinner = !!winner && !isDraw && name === winner;
    const { first, last } = splitName(name);
    const cls = [
      getFighterClass(name),
      'pk-corner',
      `pk-corner--${corner}`,
      interactive ? 'is-pickable' : '',
      isActualWinner ? 'is-won' : '',
      punched === name && isPicked ? 'pk-punch' : ''
    ].filter(Boolean).join(' ');

    return (
      <div
        className={cls}
        onClick={() => handleFighterClick(name)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFighterClick(name);
          }
        }}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-pressed={interactive ? isPicked : undefined}
      >
        {!showResult && isPicked && (
          <div className="pk-pick-tag">
            {winner && !isDraw && name !== winner ? <CrossIcon size={10} /> : <CheckIcon size={10} />}
            Your Pick
          </div>
        )}
        <div className="fighter-photo pk-well">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={name}
              decoding="async"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span className="pk-initials" style={{ display: imageUrl ? 'none' : 'flex' }}>
            {getInitials(name)}
          </span>
          {getPickBadge(name)}
          {isActualWinner && <span className="pk-win-tag">Winner</span>}
        </div>
        <div className="fighter-name">
          {first && <span className="pk-name-first">{first}</span>}
          {first && ' '}
          <span className="pk-name-last">{last}</span>
        </div>
        {(record || odds) && (
          <div className="pk-tape-stats">
            {record && <span className="fighter-record">{record}</span>}
            {odds && (
              <span className={`pk-odds ${odds.startsWith('-') ? 'is-fav' : 'is-dog'}`}>{odds}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const pickCorner = selectedWinner
    ? (selectedWinner === fighter1_name ? 'red' : selectedWinner === fighter2_name ? 'blue' : '')
    : '';
  const gradeClass = selectedWinner && winner
    ? (isDraw ? 'is-draw' : pickedCorrectWinner ? 'is-correct' : 'is-incorrect')
    : '';

  const cardClass = [
    'fight-card',
    'pk-fc',
    selectedWinner ? 'selected' : '',
    !!isTitle ? 'title' : '',
    featured ? 'pk-fc--featured' : '',
    disabled ? 'pk-fc--locked' : '',
    pickCorner ? `pk-fc--pick-${pickCorner}` : '',
    winner ? 'is-graded' : '',
    gradeClass
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClass}>
      <div className="pk-fc-head">
        <div className="fight-meta">
          {featured && <span className="pk-chip-main">Main Event</span>}
          {!!isTitle && (
            <span className="fight-badge title-fight">
              <BeltIcon />
              Title Fight
              {championshipMultiplier > 1 && ` · ${championshipMultiplier}× pts`}
            </span>
          )}
          <span className="weight-class">{weight_class}</span>
          <span className={`rounds-pill ${isTitle ? 'title' : ''}`}>{rounds} Rounds</span>
        </div>
        {!showResult && selectedWinner && (
          <span className="pk-pickset">✓ Pick Set</span>
        )}
      </div>

      <div className="fight-matchup pk-tape">
        {renderFighter('red', fighter1_name, fighter1_record, fighter1_image_url, fighter1_odds)}
        <div className="fight-vs pk-vs-slot" aria-hidden="true">
          <span className="pk-vs">VS</span>
        </div>
        {renderFighter('blue', fighter2_name, fighter2_record, fighter2_image_url, fighter2_odds)}
      </div>

      {communityStats && communityStats.total_picks > 0 && (
        <div className="community-split-bar">
          <div className="community-split-bar-labels">
            <span className="pk-split-pct is-red">{communityStats.fighter1_percentage}%</span>
            <span className="community-split-bar-caption">Community split</span>
            <span className="pk-split-pct is-blue">{communityStats.fighter2_percentage}%</span>
          </div>
          <div className="community-split-bar-track">
            <div className="community-split-bar-fill-a" style={{ width: `${communityStats.fighter1_percentage}%` }} />
            <div className="community-split-bar-fill-b" style={{ width: `${communityStats.fighter2_percentage}%` }} />
          </div>
        </div>
      )}

      {((fighter1_odds || fighter2_odds) || (showResult && winner)) && (
        <div className="fight-info">
          {showResult && winner && (
            <div className={`pk-result ${isDraw ? 'is-draw' : ''}`}>
              {isDraw ? `Result: ${method === 'NC' ? 'NC' : 'Draw'}` : (method ? `Winner: ${winner} via ${method}` : `Winner: ${winner}`)}
            </div>
          )}
          {(fighter1_odds || fighter2_odds) && (
            <div className="pk-odds-note">
              Odds subject to change
            </div>
          )}
        </div>
      )}

      {showMethod && selectedWinner && (
        <div className="method-selection">
          <div className={`method-label ${winner && !pickedCorrectWinner ? 'is-muted' : ''}`}>
            <span>{disabled ? 'Method of Victory' : 'Select Method of Victory'}</span>
            {winner && pickedCorrectWinner && pickedCorrectMethod && (
              <span className="pk-verdict is-correct">✓ Correct!</span>
            )}
            {winner && pickedCorrectWinner && !pickedCorrectMethod && selectedMethod && (
              <span className="pk-verdict is-wrong">✗ Wrong method</span>
            )}
          </div>
          <div className={`method-options ${winner && !pickedCorrectWinner ? 'is-muted' : ''}`}>
            {METHODS.map((m) => {
              const isSelected = selectedMethod === m;
              const isCorrectMethod = winner && method === m;
              const showCorrect = winner && pickedCorrectWinner && isSelected && isCorrectMethod;
              const showIncorrect = winner && pickedCorrectWinner && isSelected && !isCorrectMethod;

              return (
                <button
                  key={m}
                  type="button"
                  className={[
                    'method-btn',
                    isSelected ? 'selected' : '',
                    showCorrect ? 'is-correct' : '',
                    showIncorrect ? 'is-incorrect' : '',
                    isCorrectMethod && !isSelected ? 'is-actual' : ''
                  ].filter(Boolean).join(' ')}
                  aria-pressed={isSelected}
                  onClick={() => { if (!disabled && onMethodPick) { tapLight(); onMethodPick(m); } }}
                >
                  {m}
                  {showCorrect && <span className="pk-method-mark">✓</span>}
                  {showIncorrect && <span className="pk-method-mark">✗</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default FightCard;
